import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getErrorHttpMessage } from "utils";
import { StoreType } from "types/genericTypes";
import { useIntl } from "react-intl";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getProveedoresHttp } from "actions/proveedores";
import {
  activarEmpresaHttp,
  createEditEmpresaHttp,
  desactivarEmpresaHttp,
  getEmpresasHttp,
} from "actions/empresa";

export const useEmpresas = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const idUsuario = useSelector((state: StoreType) => state?.app?.user?.data?.id_usuario || 0);
  const idAreaDireccionUsuario = useSelector(
    (state: StoreType) => state?.app?.user?.data?.id_area || 0
  );

  const [procesando, setProcesando] = useState<boolean>(true);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [mensajeAlert, setMensajeAlert] = useState("");
  const [errorLogin, setErrorLogin] = useState(false);
  const handleisAlertOpen = () => setIsAlertOpen(true);
  const handleisAlertClose = () => setIsAlertOpen(false);

  const [verEditarEmpresa, setVerEditarEmpresa] = useState<any>(null);
  const [procesandoEmpresa, setProcesandoEmpresa] = useState<boolean>(false);
  const [isAlertOpenCrearEmpresa, setIsAlertOpenCrearEmpresa] = useState(false);
  const handleisAlertOpenCrearEmpresa = () => setIsAlertOpenCrearEmpresa(true);
  const handleisAlertCloseCrearEmpresa = () => setIsAlertOpenCrearEmpresa(false);

  const [empresas, setEmpresas] = useState<any[]>([]);

  // Estados para la paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //Modal de ver detalles de la empresa
  const [verDetalleEmpresa, setVerDetalleEmpresa] = useState<any>(null);
  const [isAlertOpenDetalleEmpresa, setIsAlertOpenDetalleEmpresa] = useState(false);
  const handleisAlertOpenDetalleEmpresa = () => setIsAlertOpenDetalleEmpresa(true);
  const handleisAlertCloseDetalleEmpresa = () => setIsAlertOpenDetalleEmpresa(false);

  //subir logo de la empresa
  const handleChangeLogo = (newFile: File | null) => {
    formik.setFieldValue("logo", newFile);
    formik.setFieldTouched("logo", true, false);
  };

  const formik = useFormik({
    initialValues: {
      clave: "",
      razon_social: "",
      nombre: "",
      rfc: "",
      direccion: "",
      contacto: "",
      correo: "",
      telefono: "",
      logo: null,
    },
    validationSchema: Yup.object({
      clave: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      razon_social: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      nombre: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      rfc: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      direccion: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      contacto: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      correo: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      telefono: Yup.number().required(intl.formatMessage({ id: "input_validation_requerido" })),
      logo: Yup.mixed(),
    }),
    validateOnMount: true,
    onSubmit: async (values) => {
      //console.log("Formulario enviado:", values);
    },
  });

  const isFieldValid = (fieldName: keyof typeof formik.values) => {
    return (
      formik.touched[fieldName] &&
      !formik.errors[fieldName] &&
      formik.values[fieldName] &&
      formik.values[fieldName] !== ""
    );
  };

  const getFieldColor = (fieldName: keyof typeof formik.values) => {
    return isFieldValid(fieldName) ? "#00AB16" : undefined;
  };

  const getEmpresas = useCallback(async () => {
    try {
      setProcesando(true);
      const empresas = await getEmpresasHttp();
      setEmpresas(empresas);
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "get_elementos_error" }));
      handleisAlertOpen();
    }
  }, []);

  const createEditEmpresa = async (datos: any) => {
    try {
      setProcesandoEmpresa(true);

      const formData = new FormData();
      formData.append("clave", datos.clave);
      formData.append("nombre", datos.nombre);
      formData.append("razon_social", datos.razon_social);
      formData.append("rfc", datos.rfc);
      formData.append("direccion", datos.direccion);
      formData.append("contacto", datos.contacto);
      formData.append("correo", datos.correo);
      formData.append("telefono", datos.telefono);
      formData.append("id_usuario", datos.id_usuario);
      if (datos.id_empresa) {
        formData.append("id_empresa", datos.id_empresa);
      }
      if (datos.logo) {
        formData.append("logo", datos.logo);
      }
      const response: any = await createEditEmpresaHttp(formData);
      setEmpresas((prevEmpresas: any[]) => {
        if (verEditarEmpresa) {
          return prevEmpresas.map((p) => (p.id === response.id ? response : p));
        }
        return [response, ...prevEmpresas];
      });
      if (verDetalleEmpresa && verDetalleEmpresa.id === response.id) {
        setVerDetalleEmpresa(response);
      }
      setMensajeAlert(
        verEditarEmpresa
          ? intl.formatMessage({ id: "general_editado_correctamente" })
          : intl.formatMessage({ id: "general_añadido_correctamente" })
      );

      if (verEditarEmpresa) {
        setVerEditarEmpresa(response);
      } else {
        formik.resetForm();
      }

      handleisAlertCloseCrearEmpresa();
      handleisAlertOpen();
      setProcesandoEmpresa(false);
    } catch (error) {
      setProcesandoEmpresa(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(
        message || verEditarEmpresa
          ? intl.formatMessage({ id: "error_editado_general" })
          : intl.formatMessage({ id: "error_añadir_general" })
      );
      handleisAlertOpen();
    }
  };

  const desactivarEmpresa = async (datos: any) => {
    try {
      setProcesando(true);
      const empresa: any = await desactivarEmpresaHttp(datos);
      setEmpresas((prevEmpresas: any[]) => {
        return prevEmpresas.map((c) => {
          if (c.id === empresa.id) {
            return {
              ...c,
              estatus: empresa.estatus,
            };
          }
          return c;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "general_desactivado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_desactivado_error" }));
      handleisAlertOpen();
    }
  };
  const activarEmpresa = async (datos: any) => {
    try {
      setProcesando(true);
      const empresa: any = await activarEmpresaHttp(datos);
      setEmpresas((prevEmpresas: any[]) => {
        return prevEmpresas.map((t) => {
          if (t.id === empresa.id) {
            return {
              ...t,
              estatus: empresa.estatus,
            };
          }
          return t;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "general_activado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_activado_error" }));
      handleisAlertOpen();
    }
  };

  useEffect(() => {
    if (verEditarEmpresa) {
      formik.setValues({
        clave: verEditarEmpresa?.clave || "",
        razon_social: verEditarEmpresa?.razon_social || "",
        nombre: verEditarEmpresa?.nombre || "",
        rfc: verEditarEmpresa?.rfc || "",
        direccion: verEditarEmpresa?.direccion || "",
        contacto: verEditarEmpresa?.contacto || "",
        correo: verEditarEmpresa?.correo || "",
        telefono: verEditarEmpresa?.telefono || 0,
        logo: null,
      });
    } else {
      formik.resetForm();
    }
  }, [verEditarEmpresa]);

  useEffect(() => {
    getEmpresas();
  }, []);

  return {
    intl,
    formik,
    procesando,
    isAlertOpen,
    mensajeAlert,
    handleisAlertClose,
    empresas,
    //crear proveedor
    isAlertOpenCrearEmpresa,
    handleisAlertOpenCrearEmpresa,
    handleisAlertCloseCrearEmpresa,
    getFieldColor,
    procesandoEmpresa,
    createEditEmpresa,
    idUsuario,
    verDetalleEmpresa,
    setVerDetalleEmpresa,
    isAlertOpenDetalleEmpresa,
    handleisAlertOpenDetalleEmpresa,
    handleisAlertCloseDetalleEmpresa,
    verEditarEmpresa,
    setVerEditarEmpresa,
    desactivarEmpresa,
    activarEmpresa,
    //imagen referencia
    handleChangeLogo,
  };
};
